// src/services/api/campaignAPI.js
import { createAuthHeaders } from '../../utils/authUtils';

const FIREBASE_FUNCTIONS_BASE_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_BASE_URL;

/**
 * Campaign API - handles campaign execution-related operations
 */
class CampaignAPI {

  // Add these methods to the existing CampaignAPI class in src/services/api/campaignAPI.js

  /**
   * Get all campaigns for the current user
   * @returns {Promise<Array>} Array of campaigns
   */
  static async getCampaigns() {
    try {
      console.log('API: Getting all campaigns');

      const headers = await createAuthHeaders();
      const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/campaigns`);
      url.searchParams.set('operation', 'getAll');

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to get campaigns');
      }

      console.log('API: Successfully retrieved campaigns', {
        count: result.data?.length || 0
      });

      return result.data || [];

    } catch (error) {
      console.error('API Error in getCampaigns:', error.message);
      throw new Error(`Failed to get campaigns: ${error.message}`);
    }
  }

  /**
   * Get a single campaign by ID
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<Object|null>} Campaign object or null
   */
  static async getCampaignById(campaignId) {
    try {
      console.log('API: Getting campaign by ID', { campaignId });

      const headers = await createAuthHeaders();
      const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/campaigns`);
      url.searchParams.set('operation', 'getById');
      url.searchParams.set('campaignId', campaignId);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to get campaign');
      }

      console.log('API: Successfully retrieved campaign', { campaignId });
      return result.data;

    } catch (error) {
      console.error('API Error in getCampaignById:', { campaignId, error: error.message });
      throw new Error(`Failed to get campaign: ${error.message}`);
    }
  }

  static async createCampaign(campaignData) {
    try {
      console.log('API: Creating campaign', { name: campaignData.name });

      const headers = await createAuthHeaders();
      const url = `${FIREBASE_FUNCTIONS_BASE_URL}/campaigns`;

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          operation: 'create',
          campaignData
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to create campaign');
      }

      console.log('API: Successfully created campaign', {
        id: result.data.id,
        name: campaignData.name
      });

      return result.data;

    } catch (error) {
      console.error('API Error in createCampaign:', {
        name: campaignData.name,
        error: error.message
      });
      throw new Error(`Failed to create campaign: ${error.message}`);
    }
  }

  static async updateCampaign(campaignId, updateData) {
    try {
      console.log('API: Updating campaign', { campaignId, fields: Object.keys(updateData) });

      const headers = await createAuthHeaders();
      const url = `${FIREBASE_FUNCTIONS_BASE_URL}/campaigns`;

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          operation: 'update',
          campaignId,
          updateData
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to update campaign');
      }

      console.log('API: Successfully updated campaign', { campaignId });
      return result.data;

    } catch (error) {
      console.error('API Error in updateCampaign:', {
        campaignId,
        error: error.message
      });
      throw new Error(`Failed to update campaign: ${error.message}`);
    }
  }

  static async deleteCampaign(campaignId) {
    try {
      console.log('API: Deleting campaign', { campaignId });

      const headers = await createAuthHeaders();
      const url = `${FIREBASE_FUNCTIONS_BASE_URL}/campaigns`;

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          operation: 'delete',
          campaignId
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to delete campaign');
      }

      console.log('API: Successfully deleted campaign', { campaignId });
      return true;

    } catch (error) {
      console.error('API Error in deleteCampaign:', {
        campaignId,
        error: error.message
      });
      throw new Error(`Failed to delete campaign: ${error.message}`);
    }
  }

  static async getCampaignExecutions(campaignId, limit = 5) {
    try {
      console.log('API: Getting campaign executions', { campaignId, limit });

      const headers = await createAuthHeaders();
      const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/campaignExecutions`);
      url.searchParams.set('campaignId', campaignId);
      url.searchParams.set('limit', limit.toString());

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to get campaign executions');
      }

      console.log('API: Successfully retrieved campaign executions', {
        campaignId,
        count: result.data?.length || 0
      });

      return result.data || [];

    } catch (error) {
      console.error('API Error in getCampaignExecutions:', {
        campaignId,
        error: error.message
      });

      throw new Error(`Failed to get campaign executions: ${error.message}`);
    }
  }

  static async getLatestExecution(campaignId) {
    try {
      const executions = await this.getCampaignExecutions(campaignId, 1);
      return executions.length > 0 ? executions[0] : null;
    } catch (error) {
      console.error('API Error in getLatestExecution:', {
        campaignId,
        error: error.message
      });
      return null;
    }
  }

  static async getMultipleCampaignExecutions(campaignIds) {
    try {
      console.log('API: Getting executions for multiple campaigns', {
        count: campaignIds.length
      });

      const headers = await createAuthHeaders();
      const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/campaignExecutions/multiple`);

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers,
        body: JSON.stringify({ campaignIds })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to get campaign executions');
      }

      console.log('API: Successfully retrieved multiple campaign executions', {
        campaignsRequested: campaignIds.length,
        executionsFound: Object.keys(result.data || {}).length
      });

      return result.data || {};

    } catch (error) {
      console.error('API Error in getMultipleCampaignExecutions:', {
        campaignIds,
        error: error.message
      });

      // Return empty object on error so components can handle gracefully
      return {};
    }
  }

  static async createCampaignPayment(campaignId) {
    try {
      console.log('API: Creating campaign payment', { campaignId });

      const headers = await createAuthHeaders();
      const url = `${FIREBASE_FUNCTIONS_BASE_URL}/campaignDispatch`;

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          operation: 'campaignPayment',
          campaignId
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to create campaign payment');
      }

      console.log('API: Successfully created campaign payment', {
        campaignId,
        paymentId: result.data.paymentId,
        expiresAt: result.data.expiresAt
      });

      return result.data;

    } catch (error) {
      console.error('API Error in createCampaignPayment:', {
        campaignId,
        error: error.message
      });
      throw new Error(`Failed to create campaign payment: ${error.message}`);
    }
  }

  static async launchCampaign(campaignId) {
    try {
      console.log('API: Launching campaign manually', { campaignId });

      const headers = await createAuthHeaders();
      const url = `${FIREBASE_FUNCTIONS_BASE_URL}/campaignDispatch`;

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          operation: 'launchCampaign',
          campaignId
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to launch campaign');
      }

      console.log('API: Successfully launched campaign', {
        campaignId,
        messagesSent: result.data?.messagesSent || 0,
        customersFound: result.data?.customersFound || 0
      });

      return result.data;

    } catch (error) {
      console.error('API Error in launchCampaign:', {
        campaignId,
        error: error.message
      });
      throw new Error(`Failed to launch campaign: ${error.message}`);
    }
  }
}

export default CampaignAPI;